import type { Root, Text, Link, PhrasingContent } from 'mdast';
import type { Plugin } from 'unified';
import { visit, SKIP, type Visitor } from 'unist-util-visit';
import { getContentScanners } from '../../../client/content-scanners';
import { encodeScanUrl } from './content-scanner-dispatch';

/**
 * Trailing punctuation that is almost always sentence punctuation rather than
 * part of a token (the period in "see /etc/hosts."). Trimmed off every match,
 * preserving the conservative behavior of the original file-path linkifier so
 * scanners don't have to encode it in their own regex.
 */
const TRAILING_PUNCT = /[.,;:!?)\]}'"`]+$/;

interface ScanMatch {
  id: string;
  value: string;
  start: number;
  end: number;
}

/**
 * Run every registered `text` scanner over `text` and return the matches that
 * survive trailing-punctuation trimming, sorted by position with overlaps
 * resolved (earliest start wins; longer match wins a tie). Cross-scanner
 * overlaps are dropped so a single character can't be claimed twice.
 */
function findMatches(text: string): ScanMatch[] {
  const scanners = getContentScanners('text');
  if (scanners.length === 0) return [];
  const found: ScanMatch[] = [];
  for (const scanner of scanners) {
    // Registry guarantees `text` scanner regexes are global, so matchAll is
    // safe and does not mutate shared `lastIndex`.
    for (const m of text.matchAll(scanner.regex)) {
      if (m.index === undefined) continue;
      const trimmed = m[0].replace(TRAILING_PUNCT, '');
      if (trimmed.length < 2) continue;
      found.push({ id: scanner.id, value: trimmed, start: m.index, end: m.index + trimmed.length });
    }
  }
  found.sort((a, b) => a.start - b.start || b.end - a.end);
  const result: ScanMatch[] = [];
  let cursor = 0;
  for (const m of found) {
    if (m.start < cursor) continue; // overlaps an already-accepted match
    result.push(m);
    cursor = m.end;
  }
  return result;
}

function splitTextNode(node: Text): PhrasingContent[] | null {
  const matches = findMatches(node.value);
  if (matches.length === 0) return null;
  const parts: PhrasingContent[] = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) {
      parts.push({ type: 'text', value: node.value.slice(cursor, m.start) });
    }
    const link: Link = {
      type: 'link',
      url: encodeScanUrl(m.id, m.value),
      title: null,
      children: [{ type: 'text', value: m.value }],
    };
    parts.push(link);
    cursor = m.end;
  }
  if (cursor < node.value.length) {
    parts.push({ type: 'text', value: node.value.slice(cursor) });
  }
  return parts;
}

/**
 * remark plugin: walk every text node and split registered `text` scanner
 * matches out into `link` nodes carrying the {@link REFLEX_SCAN_SCHEME} URL.
 * The markdown `a` slot then swaps each into the scanner's component.
 *
 * Skips text inside `link` (no double-linkify), `code`, and `inlineCode`
 * (paths/SHAs inside backticks stay as code), exactly as the original
 * file-path plugin did. Reads the registry at transform time, so it is a
 * no-op until at least one `text` scanner is registered.
 */
export const remarkContentScanners: Plugin<[], Root> = function remarkContentScanners() {
  return (tree) => {
    const visitor: Visitor<Text> = (node, index, parent) => {
      if (!parent || index === undefined) return;
      if (parent.type === 'link' || parent.type === 'code' || parent.type === 'inlineCode') {
        return SKIP;
      }
      const replacements = splitTextNode(node);
      if (!replacements) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (parent.children as any).splice(index, 1, ...replacements);
      return [SKIP, index + replacements.length];
    };
    visit(tree, 'text', visitor);
  };
};
